(() => {
  const CONFIG = {
    source: 'zomeractie_behanglijm',
    wallpaperProperty: '_zomeractie_behang',
    m2Property: '_zomeractie_m2_per_stuk',
    glueProperty: '_gratis_behanglijm_actie',
    glueVariants: [
      { id: 58402953134464, size: '2KG', coverage: 10, cost: 1900 },
      { id: 58402953167232, size: '5KG', coverage: 25, cost: 4250 },
    ],
    minimumOrderValue: 50000,
    excludedCustomerTags: ['pro partner'],
    endsAt: '2027-12-31T23:59:59+01:00',
  };

  let syncing = false;
  let syncTimer = null;

  const debugEnabled = () => {
    const params = new URLSearchParams(window.location.search);
    return params.has('zomeractie_debug') || params.get('wk_dev_mode') === 'on';
  };

  const debug = (...args) => {
    if (debugEnabled()) console.log('[Zomeractie lijm]', ...args);
  };

  const toNumber = (value) => Number(String(value || '0').replace(',', '.')) || 0;
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const isGlueVariant = (variantId) => CONFIG.glueVariants.some((variant) => variant.id === Number(variantId));
  const isCartPage = () => document.body.classList.contains('template-cart') || window.location.pathname.includes('/cart');
  const isPromotionActive = () => Date.now() < new Date(CONFIG.endsAt).getTime();
  const isCustomerExcluded = () => {
    const customerTags = window.zomeractieBehanglijmCustomer?.tags || [];
    return customerTags.some((tag) => CONFIG.excludedCustomerTags.includes(normalize(tag)));
  };

  const getSectionsToUpdate = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  const getVariantMap = (form) => {
    const mapScript = form.querySelector('[data-zomeractie-variant-map]');
    if (!mapScript) return null;

    try {
      return JSON.parse(mapScript.textContent);
    } catch (error) {
      return null;
    }
  };

  const formHasSampleVariant = (form) => {
    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    const variantMap = getVariantMap(form);
    const mappedVariant = variantMap?.variants?.find((variant) => Number(variant.id) === variantId);

    if (mappedVariant) return mappedVariant.isSample === true;

    const selectedText = Array.from(form.querySelectorAll('select option:checked, input[type="radio"]:checked'))
      .map((option) => option.textContent || option.value)
      .join(' ');

    return normalize(selectedText).includes('sample');
  };

  const updateFormProperties = (form) => {
    const properties = form.querySelectorAll('[data-zomeractie-property]');
    if (!properties.length) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    const variantMap = getVariantMap(form);
    const mappedVariant = variantMap?.variants?.find((variant) => Number(variant.id) === variantId);
    const shouldDisable = isCustomerExcluded() || !isPromotionActive() || mappedVariant?.isSample === true || formHasSampleVariant(form);
    const m2Input = form.querySelector('[name="properties[_zomeractie_m2_per_stuk]"]');

    if (m2Input && mappedVariant?.m2PerPiece) {
      m2Input.value = mappedVariant.m2PerPiece;
    }

    properties.forEach((input) => {
      input.disabled = shouldDisable;
    });
  };

  const updateAllForms = () => {
    document.querySelectorAll('form').forEach(updateFormProperties);
  };

  const lineHasProperty = (line, name, value = null) => {
    if (!line.properties) return false;
    if (!(name in line.properties)) return false;
    return value === null || String(line.properties[name]) === value;
  };

  const getLineElement = (line) => {
    if (line.key && typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      const byKey = document.querySelector(`[data-cart-line-key="${CSS.escape(line.key)}"]`);
      if (byKey) return byKey;
    }

    if (line.variant_id) {
      return document.querySelector(`[data-variant-id="${line.variant_id}"][data-zomeractie-behang="true"]`);
    }

    return null;
  };

  const getFallbackLineM2 = (line) => {
    const lineElement = getLineElement(line);
    if (!lineElement) return 0;

    const explicitM2 = toNumber(lineElement.dataset.zomeractieM2PerStuk);
    if (lineElement.dataset.zomeractieBehang === 'true' && explicitM2 > 0) return explicitM2;

    const productTags = normalize(lineElement.dataset.productTags);
    const calculatedM2 = toNumber(lineElement.dataset.zomeractieCalculatedM2);
    if (productTags.split(',').map((tag) => tag.trim()).includes('actie') && calculatedM2 > 0) {
      return calculatedM2;
    }

    return 0;
  };

  const isAutoGlueLine = (line) =>
    isGlueVariant(line.variant_id) &&
    lineHasProperty(line, CONFIG.glueProperty, 'true') &&
    lineHasProperty(line, '_actie_bron', CONFIG.source);

  const isEligibleWallpaperLine = (line) => {
    if (isGlueVariant(line.variant_id)) return false;

    const variantTitle = normalize(line.variant_title || line.title);
    if (variantTitle.includes('sample')) return false;

    return lineHasProperty(line, CONFIG.wallpaperProperty, 'true') || getFallbackLineM2(line) > 0;
  };

  const getLineM2 = (line) => getFallbackLineM2(line) || toNumber(line.properties?.[CONFIG.m2Property]);

  const chooseGlue = (requiredM2) => {
    if (requiredM2 <= 0) return {};

    const twoKg = CONFIG.glueVariants[0];
    const fiveKg = CONFIG.glueVariants[1];
    let best = null;

    const maxTwoKg = Math.ceil(requiredM2 / twoKg.coverage) + 3;
    const maxFiveKg = Math.ceil(requiredM2 / fiveKg.coverage) + 3;

    for (let quantityFiveKg = 0; quantityFiveKg <= maxFiveKg; quantityFiveKg += 1) {
      for (let quantityTwoKg = 0; quantityTwoKg <= maxTwoKg; quantityTwoKg += 1) {
        const coverage = quantityFiveKg * fiveKg.coverage + quantityTwoKg * twoKg.coverage;
        if (coverage < requiredM2) continue;

        const cost = quantityFiveKg * fiveKg.cost + quantityTwoKg * twoKg.cost;
        const overage = coverage - requiredM2;
        const packages = quantityFiveKg + quantityTwoKg;
        const candidate = { quantityTwoKg, quantityFiveKg, cost, overage, packages };

        if (
          !best ||
          candidate.cost < best.cost ||
          (candidate.cost === best.cost && candidate.overage < best.overage) ||
          (candidate.cost === best.cost && candidate.overage === best.overage && candidate.packages < best.packages)
        ) {
          best = candidate;
        }
      }
    }

    return {
      [twoKg.id]: best?.quantityTwoKg || 0,
      [fiveKg.id]: best?.quantityFiveKg || 0,
    };
  };

  const cartRoute = (routeName, fallback) => window.FoxTheme?.routes?.[routeName] || fallback;

  const parseCartResponse = async (response, action) => {
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json.status >= 400) {
      const message = json.description || json.message || `${action} is mislukt`;
      throw new Error(`${message} (${response.status})`);
    }
    return json;
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return parseCartResponse(response, 'Winkelwagen ophalen');
  };

  const publishCartUpdate = (cart) => {
    if (window.FoxTheme?.pubsub?.publish) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
  };

  const changeLine = async (lineKey, quantity) => {
    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: getSectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(cartRoute('cart_change_url', '/cart/change.js'), {
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
    });

    return parseCartResponse(response, 'Winkelwagenregel aanpassen');
  };

  const addGlueItems = async (items) => {
    const body = JSON.stringify({
      items,
      sections: getSectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(cartRoute('cart_add_url', '/cart/add.js'), {
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
    });

    return parseCartResponse(response, 'Gratis lijm toevoegen');
  };

  const buildGlueItem = (variantId, quantity) => {
    const glueVariant = CONFIG.glueVariants.find((variant) => variant.id === Number(variantId));
    return {
      id: Number(variantId),
      quantity,
      properties: {
        [CONFIG.glueProperty]: 'true',
        _actie_bron: CONFIG.source,
        _actie_naam: 'Gratis professionele behanglijm',
        _actie_dekking_m2: String(glueVariant?.coverage || ''),
      },
    };
  };

  const syncGlue = async () => {
    if (syncing) return;

    syncing = true;
    try {
      let cart = await fetchCart();
      const promotionActive = isPromotionActive();
      const customerExcluded = isCustomerExcluded();
      const cartSubtotal = Number(cart.items_subtotal_price || cart.total_price || 0);
      const meetsMinimumOrderValue = cartSubtotal >= CONFIG.minimumOrderValue;
      const eligibleLines = promotionActive && meetsMinimumOrderValue && !customerExcluded ? cart.items.filter(isEligibleWallpaperLine) : [];
      const autoGlueLines = cart.items.filter(isAutoGlueLine);
      const manualActionGlueLines = cart.items.filter((line) => isGlueVariant(line.variant_id) && !isAutoGlueLine(line));
      const requiredM2 = eligibleLines.reduce((total, line) => {
        return total + line.quantity * getLineM2(line);
      }, 0);

      const desiredQuantities = chooseGlue(requiredM2);
      let cartResponse = null;

      debug('sync start', {
        itemCount: cart.item_count,
        promotionActive,
        customerExcluded,
        cartSubtotal,
        minimumOrderValue: CONFIG.minimumOrderValue,
        meetsMinimumOrderValue,
        cartLines: cart.items.map((line) => {
          const lineElement = getLineElement(line);
          return {
            title: line.title,
            variantId: line.variant_id,
            variantTitle: line.variant_title,
            quantity: line.quantity,
            properties: line.properties,
            dom: lineElement
              ? {
                  productHandle: lineElement.dataset.productHandle || lineElement.dataset.handle,
                  productTags: lineElement.dataset.productTags,
                  lengthCm: lineElement.dataset.zomeractieLengthCm,
                  widthCm: lineElement.dataset.zomeractieWidthCm,
                  calculatedM2: lineElement.dataset.zomeractieCalculatedM2,
                  isActionWallpaper: lineElement.dataset.zomeractieBehang,
                  m2PerPiece: lineElement.dataset.zomeractieM2PerStuk,
                }
              : null,
          };
        }),
        eligibleLines: eligibleLines.map((line) => ({
          title: line.title,
          quantity: line.quantity,
          m2PerPiece: getLineM2(line),
          properties: line.properties,
          hasFallbackElement: Boolean(getLineElement(line)),
        })),
        autoGlueLines: autoGlueLines.map((line) => ({ title: line.title, quantity: line.quantity, key: line.key })),
        manualActionGlueLines: manualActionGlueLines.map((line) => ({ title: line.title, quantity: line.quantity, key: line.key })),
        requiredM2,
        desiredQuantities,
      });

      for (const line of manualActionGlueLines) {
        debug('remove manual glue line', line.key);
        cartResponse = await changeLine(line.key, 0);
      }

      for (const line of autoGlueLines) {
        const desiredQuantity = desiredQuantities[line.variant_id] || 0;
        if (line.quantity !== desiredQuantity) {
          debug('update glue line', { key: line.key, from: line.quantity, to: desiredQuantity });
          cartResponse = await changeLine(line.key, desiredQuantity);
        }
        delete desiredQuantities[line.variant_id];
      }

      const itemsToAdd = Object.entries(desiredQuantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([variantId, quantity]) => buildGlueItem(variantId, quantity));

      if (itemsToAdd.length) {
        debug('add glue items', itemsToAdd);
        cartResponse = await addGlueItems(itemsToAdd);
      }

      if (cartResponse) {
        const updatedCart = await fetchCart();
        if (cartResponse.sections) {
          updatedCart.sections = cartResponse.sections;
        }
        publishCartUpdate(updatedCart);
        if (isCartPage()) {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Zomeractie behanglijm kon niet worden bijgewerkt:', error);
    } finally {
      syncing = false;
    }
  };

  const scheduleSync = (delay = 350) => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncGlue, delay);
  };

  document.addEventListener('DOMContentLoaded', () => {
    updateAllForms();
    scheduleSync(700);
  });

  window.addEventListener('load', () => scheduleSync(300));
  window.addEventListener('pageshow', () => scheduleSync(300));

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) {
        updateFormProperties(event.target);
      }
    },
    true
  );

  document.addEventListener('change', (event) => {
    const form = event.target?.closest?.('form');
    if (form) updateFormProperties(form);

    if (event.target?.matches?.('cart-items input[name="updates[]"], input[name="updates[]"]')) {
      scheduleSync(900);
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target?.closest?.('cart-remove-item, .cart-item__remove, quantity-input, .quantity__button')) {
      scheduleSync(900);
    }
  });

  document.addEventListener('product-ajax:added', () => scheduleSync(450));
  document.addEventListener('cart:drawer:open', () => scheduleSync(500));
  document.addEventListener('cart:updated', () => scheduleSync(700));

  window.zomeractieBehanglijm = {
    sync: syncGlue,
    chooseGlue,
    isPromotionActive,
    config: CONFIG,
  };
})();
